/**
 * Pure layout helpers for Drawer -- extracted per the >150-line
 * business-logic-extraction rule (see repo CLAUDE.md "Component
 * Testability + Stories"). Keeps drawer.tsx to JSX + event wiring.
 */

/**
 * Which edge the drawer panel slides in from.
 *
 * `'end'` is the original (and default) right-side slide-over -- every
 * existing consumer gets byte-identical behavior. `'bottom'` renders a
 * bottom sheet, used by `AdaptiveDialog`'s phone presentation.
 */
export type DrawerSide = 'end' | 'bottom';

/** Fraction of the visible viewport a bottom sheet may occupy. */
const BOTTOM_SHEET_VIEWPORT_FRACTION = 0.9;

/** Static fallback when the Visual Viewport API is unavailable (SSR, or an
 * older browser) -- matches the fraction used once real viewport data
 * arrives, so there's no visible jump when the hook's first effect runs. */
const BOTTOM_SHEET_FALLBACK_MAX_HEIGHT = '90vh';

/**
 * Computes the bottom-sheet panel's `max-height` from the live visual
 * viewport height (shrinks when an on-screen keyboard opens), bounding the
 * panel to (a fraction of) the space actually visible.
 *
 * This caps the panel's SIZE but says nothing about its POSITION -- on
 * iOS Safari the on-screen keyboard does not shrink the layout viewport
 * (only `visualViewport` shrinks and pans via `offsetTop`), so a
 * bottom-anchored panel still needs `get_bottom_sheet_keyboard_offset`
 * below to actually stay above the keyboard; the max-height cap alone
 * would just shrink the panel from the top while its bottom edge stayed
 * glued behind the keyboard. Combine both.
 *
 * Only used for `side="bottom"` -- the `'end'` side keeps its historic
 * `height: 100%` untouched, by design (D2: zero change for existing
 * consumers).
 */
export function get_bottom_sheet_max_height(
  visualViewportHeight: number | null,
): string {
  if (visualViewportHeight === null) return BOTTOM_SHEET_FALLBACK_MAX_HEIGHT;
  const rounded = Math.round(
    visualViewportHeight * BOTTOM_SHEET_VIEWPORT_FRACTION,
  );
  return `${String(rounded)}px`;
}

/** The subset of `VisualViewportState` (see `use_visual_viewport.ts`)
 * needed to compute the bottom sheet's keyboard offset. */
export interface VisualViewportGeometry {
  height: number | null;
  offsetTop: number;
  windowInnerHeight: number | null;
}

/**
 * How far (in CSS px) to shift a bottom-anchored panel UP so its bottom
 * edge tracks the bottom edge of the *visible* visual viewport
 * (`offsetTop + height`) instead of the bottom edge of the full layout
 * viewport (`windowInnerHeight`).
 *
 * Why this is needed: the Drawer's overlay is `position: fixed; inset: 0`
 * (sized to the layout viewport) with the panel flex-pinned to its bottom
 * edge. On iOS Safari, opening the on-screen keyboard does NOT shrink the
 * layout viewport -- only `visualViewport.height` shrinks, and
 * `visualViewport.offsetTop` pans as iOS scrolls the focused input into
 * view. Without this offset, the panel's bottom edge stays glued to the
 * layout viewport's bottom edge, which ends up behind the keyboard.
 *
 * Worked example (iPhone 12/13/14, matching the adversarial review that
 * caught the original bug): `windowInnerHeight = 844`, keyboard height
 * ≈336, so `visualViewport.height ≈ 508` and (no scroll pan)
 * `offsetTop = 0`. Offset = 844 - (0 + 508) = 336 -- shifting the panel up
 * by 336px puts its bottom edge at y=508, exactly the bottom of the
 * visible area, so the footer is fully visible above the keyboard.
 *
 * Returns `'0px'` (no shift) when any input is unavailable (SSR, a
 * browser without Visual Viewport support -- in which case
 * `get_bottom_sheet_max_height`'s static `90vh` fallback is also in
 * effect) or when the computed offset would be negative (the keyboard is
 * closed and the visual viewport already fills the layout viewport --
 * never push the sheet down below its resting position).
 *
 * Apply via `marginBottom` on the panel, not `transform: translateY(...)`
 * -- the panel's own open/close animation already drives `transform` via
 * CSS keyframes (see `drawer-slide-in-bottom` in drawer.tsx), and
 * `margin-bottom` composes with the overlay's flexbox layout without
 * fighting that animation.
 */
export function get_bottom_sheet_keyboard_offset(
  geometry: VisualViewportGeometry,
): string {
  const { height, offsetTop, windowInnerHeight } = geometry;
  if (height === null || windowInnerHeight === null) return '0px';
  const offset = windowInnerHeight - (offsetTop + height);
  const clamped = Math.max(0, Math.round(offset));
  return `${String(clamped)}px`;
}

/** CSS `animation` shorthand for the panel's open transition, per side. */
export function get_panel_animation(side: DrawerSide): string {
  return side === 'bottom'
    ? 'drawer-slide-in-bottom 0.2s cubic-bezier(0,0,0.2,1)'
    : 'drawer-slide-in 0.2s cubic-bezier(0,0,0.2,1)';
}
